import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, AlertCircle, Trash2, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "./adminApi";

export default function EmailLogsTab() {
  const [logs, setLogs] = useState([]);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [previewLog, setPreviewLog] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fetchLogs = useCallback(async () => { try { const r = await api.get("/email-logs"); setLogs(r.data); } catch {} }, []);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handlePurge = async () => {
    if (!window.confirm("Supprimer les logs de plus de 60 jours ?")) return;
    try { const r = await api.delete("/email-logs/purge"); toast.success(r.data.message); fetchLogs(); } catch { toast.error("Erreur"); }
  };

  const handleRetry = async (log) => {
    setRetrying(log.log_id);
    try {
      await api.post(`/email-logs/${log.log_id}/retry`);
      toast.success(`Email renvoyé à ${log.to_email}`);
      setTimeout(fetchLogs, 1500);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setRetrying(null); }
  };

  const handleRetryAll = async () => {
    const errorCount = logs.filter((l) => l.status !== "sent").length;
    if (!errorCount) return;
    if (!window.confirm(`Renvoyer les ${errorCount} email(s) en erreur ?\n\nLes adresses de test (@test.com, @example.com, etc.) seront automatiquement ignorées pour éviter les bounces.`)) return;
    try {
      const r = await api.post("/email-logs/retry-all-errors");
      const skipped = r.data.skipped_test || 0;
      toast.success(`${r.data.count} email(s) en cours de renvoi${skipped > 0 ? `, ${skipped} adresse(s) de test ignorée(s)` : ""}`);
      setTimeout(fetchLogs, 5000);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDeleteAllErrors = async () => {
    const errorCount = logs.filter((l) => l.status !== "sent").length;
    if (!errorCount) return;
    if (!window.confirm(`⚠️ Supprimer définitivement les ${errorCount} log(s) en erreur ?\n\nUtile pour nettoyer la liste après un incident (les emails ne seront plus renvoyés).`)) return;
    try {
      const r = await api.delete("/email-logs/errors");
      toast.success(r.data.message);
      fetchLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openPreview = async (log) => {
    setPreviewLoading(true);
    setPreviewLog({ ...log, html_body: undefined });
    try {
      const r = await api.get(`/email-logs/${log.log_id}`);
      setPreviewLog(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de récupérer le contenu");
    } finally {
      setPreviewLoading(false);
    }
  };

  const errorCount = logs.filter((l) => l.status !== "sent").length;
  const filteredLogs = showOnlyErrors ? logs.filter((l) => l.status !== "sent") : logs;

  return (
    <div>
      {errorCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-3 mb-4 text-xs text-amber-800" data-testid="throttling-info">
          <strong>ℹ️ À propos des erreurs « ApplicationThrottled »</strong> : Microsoft Graph limite le nombre d'emails simultanés. Le système retry désormais automatiquement (jusqu'à 5 tentatives) sur les nouveaux envois. Les anciennes erreurs ci-dessous peuvent être relancées via le bouton <strong>Renvoyer</strong> ou <strong>Renvoyer tous</strong>.
        </div>
      )}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-gray-500">{logs.length} email(s) — <span className={errorCount > 0 ? "text-red-600 font-semibold" : "text-gray-400"}>{errorCount} en erreur</span></p>
          {errorCount > 0 && (
            <Button variant="outline" onClick={() => setShowOnlyErrors((v) => !v)} className={`rounded-none text-xs h-8 ${showOnlyErrors ? "border-red-500 text-red-600" : "border-gray-300"}`} data-testid="toggle-errors-only">
              <AlertCircle className="w-3 h-3 mr-1" /> {showOnlyErrors ? `Voir tous (${logs.length})` : `Erreurs uniquement (${errorCount})`}
            </Button>
          )}
          {errorCount > 0 && (
            <Button onClick={handleRetryAll} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-8" data-testid="retry-all-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Renvoyer tous ({errorCount})
            </Button>
          )}
          {errorCount > 0 && (
            <Button onClick={handleDeleteAllErrors} variant="outline" className="rounded-none text-xs h-8 border-red-500 text-red-600 hover:bg-red-50" data-testid="delete-all-errors-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Supprimer tous ({errorCount})
            </Button>
          )}
        </div>
        <Button variant="outline" onClick={handlePurge} className="rounded-none text-xs border-red-300 text-red-500" data-testid="purge-logs-btn">Purger ({">"}60 jours)</Button>
      </div>
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {filteredLogs.map((log) => (
          <button
            type="button"
            key={log.log_id}
            onClick={() => openPreview(log)}
            className="w-full text-left bg-white border border-gray-200 p-3 flex items-center justify-between gap-3 text-sm hover:bg-[#FAFAFA] transition-colors cursor-pointer"
            data-testid={`log-${log.log_id}`}
            title="Cliquer pour afficher le contenu du mail"
          >
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[#3F3F3F] truncate flex items-center gap-1">
                <Eye className="w-3 h-3 text-gray-400 shrink-0" />
                {log.subject}
              </p>
              <p className="text-xs text-gray-500 truncate">{log.from_email} → {log.to_email}</p>
              <p className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString("fr-FR")}</p>
              {log.status !== "sent" && log.error && (
                <p className="text-[11px] text-red-500 mt-1 font-mono truncate" title={log.error}>{log.error}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
              {log.status !== "sent" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={retrying === log.log_id}
                  onClick={(e) => { e.stopPropagation(); handleRetry(log); }}
                  className="rounded-none border-[#662483] text-[#662483] text-xs h-7"
                  data-testid={`retry-email-${log.log_id}`}
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${retrying === log.log_id ? "animate-spin" : ""}`} />
                  {retrying === log.log_id ? "Envoi…" : "Renvoyer"}
                </Button>
              )}
              <Badge className={`rounded-none text-xs ${log.status === "sent" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{log.status === "sent" ? "Envoyé" : "Erreur"}</Badge>
            </div>
          </button>
        ))}
        {filteredLogs.length === 0 && <div className="text-center py-8 text-gray-500 text-sm">{showOnlyErrors ? "Aucun email en erreur" : "Aucun email envoyé"}</div>}
      </div>

      {/* Preview modal */}
      <Dialog open={!!previewLog} onOpenChange={(v) => !v && setPreviewLog(null)}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="email-preview-dialog">
          <DialogHeader className="border-b border-gray-200 px-6 py-4 shrink-0">
            <DialogTitle className="text-[#3F3F3F] text-base pr-8 break-words">
              {previewLog?.subject || "Email"}
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              <span className="font-mono">{previewLog?.from_email}</span> → <span className="font-mono">{previewLog?.to_email}</span>
              <span className="text-gray-400"> · {previewLog?.created_at ? new Date(previewLog.created_at).toLocaleString("fr-FR") : ""}</span>
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
            {previewLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement du contenu…
              </div>
            ) : previewLog?.html_body ? (
              <iframe
                title="Aperçu email"
                srcDoc={previewLog.html_body}
                sandbox=""
                className="w-full bg-white border border-gray-200"
                style={{ minHeight: "60vh" }}
                data-testid="email-preview-iframe"
              />
            ) : (
              <div className="bg-white border border-gray-200 p-6 text-center text-sm text-gray-500">
                Aucun aperçu disponible pour ce log (email antérieur à la mise en place de la sauvegarde du contenu).
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
